import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

@ngCore.Component({
  imports: [ds.chart.Component],
  selector: 'atn-mosaic-chart-donut',
  standalone: true,
  styleUrl: './donut.component.scss',
  templateUrl: './donut.component.html',
})
export class AtnMosaicChartDonutComponent {
  /** Min value in pixels for chart height */
  public readonly minHeight = 300
  /** Min value in pixels for chart width */
  public readonly minWidth = 300
  /**
   * Input property for widget rendering data.
   *
   * This property accepts data of type {@link ents.core.mosaic.Widget.Rendering}
   * Represents the rendering information for a Bar chart widget.
   */
  public chartsSg = ngCore.input.required<
    ents.core.display.chart.Chart<ents.core.display.chart.Type.Donut>[]
  >({ alias: 'charts' })

  /** Reference to container element which is wrapping all charts*/
  private readonly _donutChartsContainerSg = ngCore.viewChild<
    ngCore.ElementRef<HTMLElement>
  >('donutChartsContainer')

  /** Computed height and width dimensions for container element*/
  private readonly _containerDimensionsSg = ngCore.computed(
    (donutChartsContainer = this._donutChartsContainerSg()) => {
      if (donutChartsContainer) {
        const { width, height } =
          donutChartsContainer.nativeElement.getBoundingClientRect()

        return { height, width }
      }
      return undefined
    },
  )

  /** Initialized palette with colors that will be assigned to chart elements */
  private readonly _palette = new ds.color.Palette({
    colors: ds.color.Legacy.Main.ALL,
  })

  // TODO: Resizing charts should be supported on window resize

  /** Charts count */
  public readonly chartsCountSg = ngCore.computed(
    () => this.chartsSg()?.length ?? 0,
  )

  /** Computed value of donut charts container height */
  public readonly containerHeightSg = ngCore.computed(
    (containerDimensions = this._containerDimensionsSg()) =>
      containerDimensions?.height ?? 0,
  )

  /**
   * Calculated number of columns that will be rendered.
   * If there is more than one chart that needs to be rendered, we need to
   * calculate number of columns that will be rendered inside the css grid.
   */
  public readonly columnsCountSg = ngCore.computed(
    (
      containerDimensions = this._containerDimensionsSg(),
      chartsCount = this.chartsCountSg(),
    ) => {
      if (!containerDimensions) {
        return 0
      }
      const { height, width } = containerDimensions

      /**
       * Max number of columns that can be rendered in widget, based on value of
       * minimum width for chart.
       */
      let columnsCount = Math.floor(width / this.minWidth)

      /**
       * Number of rows that can be rendered in widget, based on value of
       * minimum height for chart.
       */
      const rowsCount = Math.floor(height / this.minHeight)

      /**
       * Optimizes the chart layout based on available container space.
       *
       * The optimization addresses two key scenarios:
       *
       * 1. When the number of chart columns equals the total number of charts:
       *    We check if there would be empty vertical space at the bottom of the display area.
       *    This occurs when the theoretical maximum number of rows (based on container height)
       *    exceeds the actual rows needed to display all charts.
       *
       * 2. When there's significant unused space in the grid layout:
       *    We calculate the total available grid spaces (columns × rows) and compare it
       *    with the number of charts. If there are more than 1 empty spaces, we reduce
       *    the number of columns to create wider, more readable charts.
       *
       * Example:
       * - Container dimensions: 1000px × 800px
       * - Charts to display: 3
       * - Max columns (based on min chart width 300px): 1000/300 = 3
       * - Max rows (based on min chart height 300px): 800/300 = 2
       * - Total grid spaces: 3 columns × 2 rows = 6 spaces
       *
       * With 3 charts in a 6-space grid, all charts would appear in the first row,
       * leaving the second row empty. By reducing to 2 columns, we create a more
       * balanced 2×2 grid with 2 charts in row 1 and 1 chart in row 2, making
       * each chart larger and improving data visualization.
       */
      if (
        (columnsCount === chartsCount &&
          rowsCount > Math.ceil(chartsCount / columnsCount)) ||
        columnsCount * rowsCount - chartsCount > 1
      ) {
        // If number of charts is 2, we want number of columns to be 2, because
        // it is preferred horizontally layout in that case
        if (chartsCount === 2) {
          return chartsCount
        }
        /**
         * Reduces the number of columns to optimize chart layout.
         *
         * This optimization process decreases the column count when there's unused vertical space,
         * making each chart wider and more readable. The algorithm continues reducing columns
         * until one of these conditions is met:
         *
         * 1. We reach the minimum of 1 column (can't reduce further)
         * 2. The layout becomes efficient with minimal empty space
         */

        while (columnsCount * rowsCount - chartsCount > 1) {
          columnsCount = columnsCount > 1 ? columnsCount - 1 : columnsCount
        }
        // After number of columns is reduced, and we get into the case that
        // number of charts is lower than number of columns, we adjust the
        // column count to match the number of charts instead of using the
        // maximum.
        // This prevents having empty columns and allows each chart to utilize
        // more horizontal space.
        return chartsCount < columnsCount ? chartsCount : columnsCount
      }

      /**
       * In case the number of charts is lower than number of columns calculated
       * by min width of container, number of columns should be equal to number
       * of charts.
       */
      if (chartsCount < columnsCount) {
        return chartsCount
      }

      return columnsCount
    },
  )

  /** Number of rows calculated by number of charts and number of columns */
  public readonly rowsCountSg = ngCore.computed(
    (
      chartsCount = this.chartsCountSg(),
      columnsCount = this.columnsCountSg(),
    ) =>
      chartsCount && columnsCount ? Math.ceil(chartsCount / columnsCount) : 0,
  )
}
